<template>
	<JBox>
		<!-- State switcher -->
		<CFormInput
			name="state"
			componentName="JRadios"
			v-model="state"
			:options="[
				{ name: 'Normal', value: 'normal' },
				{ name: 'Hover', value: 'hover' }
			]"
			@input="handleInput"
			class="s-mb-12"
		/>
		<!-- ViewPort Radios -->
		<CFormInput
			name="viewport"
			v-model="selectedViewport"
			:label="this.$t('uiEditor.viewport')"
			componentName="JRadios"
			:options="[
				{ name: '—', value: 'all' },
				{ name: 'SM', value: 'sm' },
				{ name: 'MD', value: 'md' },
				{ name: 'LG', value: 'lg' },
				{ name: 'XL', value: 'xl' }
			]"
			@input="handleViewportChange"
			class="s-mb-6"
		/>

		<!-- Normal -->
		<template v-if="state === 'normal'">
			<JBox v-for="(propertyGroup, index) in Object.keys(propertyGroups.normal)" :key="`Group-${index}`" class=" s-mb-8">
				<!-- Title of collapse  -->
				<JBox
					class="s-cursor-pointer s-text-xs s-uppercase s-text-gray-600 s-border-b s-border-gray-300 s-mb-4 s-py-2 s-font-bold "
					@click="clickGroups(propertyGroup)"
				>
					{{ propertyGroup }}
				</JBox>

				<JFlex v-if="selectedGroups.includes(propertyGroup)" class="s-w-full s-flex-wrap">
					<!-- Iterate all properties in the same group -->
					<JBox v-for="property in propertyGroups.normal[propertyGroup]" :key="`Property-${property}`" class="s-mb-6 s-w-full">
						<CFormInput
							:name="`${property}`"
							componentName="JSelect"
							:value="computedVModel(property)"
							:label="properties[`${property}`].label"
							:options="properties[`${property}`].values"
							@input="handleInput(property, $event)"
						/>
					</JBox>
				</JFlex>
			</JBox>
		</template>

		<!-- Hover -->
		<template v-else>
			<JBox v-for="(propertyGroup, index) in Object.keys(propertyGroups.hover)" :key="`Group-${index}`" class="s-mb-8">
				<!-- Title of collapse  -->
				<JBox
					class="s-cursor-pointer s-text-xs s-uppercase s-text-gray-600 s-border-b s-border-gray-300 s-mb-4 s-py-2 s-font-bold "
					@click="clickGroups(propertyGroup)"
				>
					{{ propertyGroup }}
				</JBox>
				<JFlex v-if="selectedGroups.includes(propertyGroup)" class="s-w-full s-flex-wrap">
					<!-- Iterate all properties in the same group -->
					<JBox v-for="property in propertyGroups.hover[propertyGroup]" :key="`Property-${property}`" class="s-mb-6 s-w-full">
						<CFormInput
							:name="`${property}`"
							componentName="JSelect"
							:label="properties[`${property}`].label"
							:value="computedVModel(property)"
							:options="properties[`${property}`].values"
							@input="handleInput(property, $event)"
						/>
					</JBox>
				</JFlex>
			</JBox>
		</template>
	</JBox>
</template>

<script>
/**
 * Some parts of tailwind config
 * We use config from CoverQuote
 * Useful for converting color and other tokens into names
 */
const tailwindConfig = {
	colors: {
		c0: "var(--color0)",
		c1: "var(--color1)",
		c2: "var(--color2)",
		c3: "var(--color3)",
		c4: "var(--color4)",
		c5: "var(--color5)",
		c6: "var(--color6)",
		c7: "var(--color7)",
		c8: "var(--color8)",
		c9: "var(--color9)",
		c10: "var(--color10)",
		c11: "var(--color11)",
		c12: "var(--color12)",
		c13: "var(--color13)",
		c14: "var(--color14)",
		c15: "var(--color15)",
		c16: "var(--color16)",
		cWhite: "var(--colorWhite)",
		cBlack: "var(--colorBlack)",
		"cBlack-a50": "var(--colorBlack-a50)",
		cError: "var(--colorError)",
		cSuccess: "var(--colorSuccess)",
		cWarning: "var(--colorWarning)"
	},
	fontFamily: {
		primary: "var(--fontFamilyPrimary)",
		secondary: "var(--fontFamilySecondary)",
		tertiary: "var(--fontFamilyTertiary)"
	},
	shadows: {
		"1": "var(--shadow1)",
		"2": "var(--shadow2)",
		"3": "var(--shadow3)",
		"4": "var(--shadow4)",
		"5": "var(--shadow5)",
		focus: "var(--shadowFocus)",
		none: "var(--shadowNone)"
	},
	transitions: {
		xshort: "var(--transition-xshort)",
		short: "var(--transition-short)",
		medium: "var(--transition-medium)",
		long: "var(--transition-long)",
		xlong: "var(--transition-xlong)"
	}
}

export default {
	props: {
		// Classes that would be defined in DOM like class="bg-c1 text-sm"
		value: {
			type: String,
			default: () => ""
		},

		// Tenant theming
		theme: {
			type: Object,
			// HACK: just for testing a fragment of tenant theming
			default: () => ({})
		}
	},

	data() {
		return {
			state: "normal",
			selectedGroups: [],
			classesObject: {},
			tailwindScaleLadder: [0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48, 56, 64],
			selectedViewport: "all"
		}
	},

	computed: {
		/**
		 * Set of properties that user can edit
		 * Should be the same name as js css
		 */
		properties() {
			return {
				// Normal
				fontSize: {
					group: "Text",
					label: "Text Size",
					values: [
						{ name: "xs", value: `text-xs` },
						{ name: "sm", value: `text-sm` },
						{ name: "base", value: `text-base` },
						{ name: "lg", value: `text-lg` },
						{ name: "xl", value: `text-xl` },
						{ name: "2xl", value: `text-2xl` },
						{ name: "3xl", value: `text-3xl` },
						{ name: "4xl", value: `text-4xl` },
						{ name: "5xl", value: `text-5xl` },
						{ name: "6xl", value: `text-6xl` }
					]
				},
				fontWeight: {
					group: "Text",
					label: "Text Weight",
					values: [
						{ name: "hairline", value: `font-hairline` },
						{ name: "thin", value: `font-thin` },
						{ name: "light", value: `font-light` },
						{ name: "normal", value: `font-normal` },
						{ name: "medium", value: `font-medium` },
						{ name: "semibold", value: `font-semibold` },
						{ name: "bold", value: `font-bold` },
						{ name: "extrabold", value: `font-extrabold` },
						{ name: "black", value: `font-black` }
					]
				},
				color: {
					group: "Text",
					label: "Text Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `text-${key}`
					}))
				},

				textAlignment: {
					group: "Text",
					label: "Text Alignment",
					values: [
						{ name: "left", value: `text-left` },
						{ name: "center", value: `text-center` },
						{ name: "right", value: `text-right` },
						{ name: "justify", value: `text-justify` }
					]
				},

				letterSpacing: {
					group: "Text",
					label: "Letter Spacing",
					values: [
						{ name: "tighter", value: `tracking-tighter` },
						{ name: "tight", value: `tracking-tight` },
						{ name: "normal", value: `tracking-normal` },
						{ name: "wide", value: `tracking-wide` },
						{ name: "wider", value: `tracking-wider` },
						{ name: "widest", value: `tracking-widest` }
					]
				},
				lineHeight: {
					group: "Text",
					label: "Line Height",
					values: [
						{ name: "none", value: `leading-none` },
						{ name: "tight", value: `leading-tight` },
						{ name: "snug", value: `leading-snug` },
						{ name: "normal", value: `leading-normal` },
						{ name: "relaxed", value: `leading-relaxed` },
						{ name: "loose", value: `leading-loose` },
						{ name: "3", value: `leading-3` },
						{ name: "4", value: `leading-4` },
						{ name: "5", value: `leading-5` },
						{ name: "6", value: `leading-6` },
						{ name: "7", value: `leading-7` },
						{ name: "8", value: `leading-8` },
						{ name: "9", value: `leading-9` },
						{ name: "10", value: `leading-10` }
					]
				},
				fontFamily: {
					group: "Font",
					label: "Font Family",
					values: Object.keys(tailwindConfig?.fontFamily ?? {}).map(key => ({
						name: this.getThemePropertyName("fontFamily", key),
						value: `font-${key}`
					}))
				},

				// Height & Width
				height: {
					group: "Height & Width",
					label: "Height",
					values: [
						{ name: "auto", value: `h-auto` },
						...this.tailwindScaleLadder.map(scale => ({
							name: scale,
							value: `h-${scale}`
						})),
						{ name: "full", value: `h-full` },
						{ name: "screen", value: `h-screen` }
					]
				},
				width: {
					group: "Height & Width",
					label: "Width",
					values: [
						{ name: "auto", value: `w-auto` },
						...this.tailwindScaleLadder.map(scale => ({
							name: scale,
							value: `w-${scale}`
						})),
						{ name: "1/5", value: `w-1/5` },
						{ name: "2/5", value: `w-2/5` },
						{ name: "3/5", value: `w-3/5` },
						{ name: "4/5", value: `w-4/5` },
						{ name: "1/12", value: `w-1/12` },
						{ name: "2/12", value: `w-2/12` },
						{ name: "3/12", value: `w-3/12` },
						{ name: "4/12", value: `w-4/12` },
						{ name: "5/12", value: `w-5/12` },
						{ name: "6/12", value: `w-6/12` },
						{ name: "7/12", value: `w-7/12` },
						{ name: "8/12", value: `w-8/12` },
						{ name: "9/12", value: `w-9/12` },
						{ name: "10/12", value: `w-10/12` },
						{ name: "11/12", value: `w-11/12` },
						{ name: "full", value: `w-full` },
						{ name: "screen", value: `w-screen` }
					]
				},
				maxHeight: {
					group: "Height & Width",
					label: "Max Height",
					values: [
						{ name: "full", value: `max-h-full` },
						{ name: "screen", value: `max-h-screen` }
					]
				},
				maxWidth: {
					group: "Height & Width",
					label: "Max Width",
					values: [
						{ name: "xs", value: `max-w-xs` },
						{ name: "sm", value: `max-w-sm` },
						{ name: "md", value: `max-w-md` },
						{ name: "lg", value: `max-w-lg` },
						{ name: "xl", value: `max-w-xl` },
						{ name: "2xl", value: `max-w-2xl` },
						{ name: "3xl", value: `max-w-3xl` },
						{ name: "4xl", value: `max-w-4xl` },
						{ name: "5xl", value: `max-w-5xl` },
						{ name: "6xl", value: `max-w-6xl` },
						{ name: "full", value: `max-w-full` }
					]
				},
				minHeight: {
					group: "Height & Width",
					label: "Min Height",
					values: [
						{ name: "0", value: `min-h-0` },
						{ name: "full", value: `min-h-full` },
						{ name: "screen", value: `min-h-screen` }
					]
				},
				minWidth: {
					group: "Height & Width",
					label: "Min Width",
					values: [
						{ name: "0", value: `min-w-0` },
						{ name: "full", value: `min-w-full` }
					]
				},

				backgroundPosition: {
					group: "Background",
					label: "Position",
					values: [
						{ name: "bottom", value: `bg-bottom` },
						{ name: "center", value: `bg-center` },
						{ name: "left", value: `bg-left` },
						{ name: "left-bottom", value: `bg-left-bottom` },
						{ name: "left-top", value: `bg-left-top` },
						{ name: "right", value: `bg-right` },
						{ name: "right-bottom", value: `bg-right-bottom` },
						{ name: "right-top", value: `bg-right-top` },
						{ name: "top", value: `bg-top` }
					]
				},
				backgroundSize: {
					group: "Background",
					label: "Size",
					values: [
						{ name: "auto", value: `bg-auto` },
						{ name: "cover", value: `bg-cover` },
						{ name: "contain", value: `bg-contain` }
					]
				},
				backgroundColor: {
					group: "Background",
					label: "Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `bg-${key}`
					}))
				},

				boxShadow: {
					group: "Box",
					label: "Box Shadow",
					values: Object.keys(tailwindConfig?.shadows ?? {}).map(key => ({
						name: this.getThemePropertyName("shadows", key),
						value: `shadow-${key}`
					}))
				},

				borderWidth: {
					group: "Border",
					label: "Border",
					values: [
						{ name: "0", value: `border-0` },
						{ name: "1", value: `border` },
						{ name: "2", value: `border-2` },
						{ name: "4", value: `border-4` },
						{ name: "8", value: `border-8` }
					]
				},
				borderTop: {
					group: "Border",
					label: "Border Top",
					values: [
						{ name: "0", value: `border-t-0` },
						{ name: "1", value: `border-t` },
						{ name: "2", value: `border-t-2` },
						{ name: "4", value: `border-t-4` },
						{ name: "8", value: `border-t-8` }
					]
				},
				borderRight: {
					group: "Border",
					label: "Border Right",
					values: [
						{ name: "0", value: `border-r-0` },
						{ name: "1", value: `border-r` },
						{ name: "2", value: `border-r-2` },
						{ name: "4", value: `border-r-4` },
						{ name: "8", value: `border-r-8` }
					]
				},
				borderBottom: {
					group: "Border",
					label: "Border Bottom",
					values: [
						{ name: "0", value: `border-b-0` },
						{ name: "1", value: `border-b` },
						{ name: "2", value: `border-b-2` },
						{ name: "4", value: `border-b-4` },
						{ name: "8", value: `border-b-8` }
					]
				},
				borderLeft: {
					group: "Border",
					label: "Border Left",
					values: [
						{ name: "0", value: `border-l-0` },
						{ name: "1", value: `border-l` },
						{ name: "2", value: `border-l-2` },
						{ name: "4", value: `border-l-4` },
						{ name: "8", value: `border-l-8` }
					]
				},
				borderColor: {
					group: "Border",
					label: "Border Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `border-${key}`
					}))
				},
				borderStyle: {
					group: "Border",
					label: "Border Style",
					values: [
						{ name: "solid", value: `border-solid` },
						{ name: "dashed", value: `border-dashed` },
						{ name: "dotted", value: `border-dotted` },
						{ name: "double", value: `border-double` },
						{ name: "none", value: `border-none` }
					]
				},
				borderRadius: {
					group: "Border",
					label: "Border Radius",
					values: [
						{ name: "none", value: `rounded-none` },
						{ name: "sm", value: `rounded-sm` },
						{ name: "normal", value: `rounded` },
						{ name: "md", value: `rounded-md` },
						{ name: "lg", value: `rounded-lg` },
						{ name: "full", value: `rounded-full` }
					]
				},

				margin: {
					group: "Margin",
					label: "Margin All",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `m-${scale}`
					}))
				},
				marginTop: {
					group: "Margin",
					label: "Margin Top",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `mt-${scale}`
					}))
				},
				marginRight: {
					group: "Margin",
					label: "Margin Right",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `mr-${scale}`
					}))
				},
				marginBottom: {
					group: "Margin",
					label: "Margin Bottom",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `mb-${scale}`
					}))
				},
				marginLeft: {
					group: "Margin",
					label: "Margin Left",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `ml-${scale}`
					}))
				},

				padding: {
					group: "Padding",
					label: "Padding All",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `p-${scale}`
					}))
				},
				paddingTop: {
					group: "Padding",
					label: "Padding Top",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `pt-${scale}`
					}))
				},
				paddingRight: {
					group: "Padding",
					label: "Padding Right",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `pr-${scale}`
					}))
				},
				paddingBottom: {
					group: "Padding",
					label: "Padding Bottom",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `pb-${scale}`
					}))
				},
				paddingLeft: {
					group: "Padding",
					label: "Padding Left",
					values: this.tailwindScaleLadder.map(scale => ({
						name: scale,
						value: `pl-${scale}`
					}))
				},

				// Flex
				flex: {
					group: "Flex",
					label: "Flex",
					values: [
						{ name: "initial", value: `flex-initial` },
						{ name: "1", value: `flex-1` },
						{ name: "auto", value: `flex-auto` },
						{ name: "none", value: `flex-none` }
					]
				},
				flexGrow: {
					group: "Flex",
					label: "Flex Grow",
					values: [
						{ name: "default", value: `flex-grow` },
						{ name: "0", value: `flex-grow-0` }
					]
				},
				flexShrink: {
					group: "Flex",
					label: "Flex Shrink",
					values: [
						{ name: "default", value: `flex-shrink` },
						{ name: "0", value: `flex-shrink-0` }
					]
				},
				justifyContent: {
					group: "Flex",
					label: "Justify Content",
					values: [
						{ name: "start", value: `justify-start` },
						{ name: "center", value: `justify-center` },
						{ name: "end", value: `justify-end` },
						{ name: "between", value: `justify-between` },
						{ name: "around", value: `justify-around` }
					]
				},
				alignItmes: {
					group: "Flex",
					label: "Align Items",
					values: [
						{ name: "start", value: `items-start` },
						{ name: "center", value: `items-center` },
						{ name: "end", value: `items-end` },
						{ name: "stretch", value: `items-stretch` },
						{ name: "baseline", value: `items-baseline` }
					]
				},

				transitionProperty: {
					group: "Transition",
					label: "Property",
					values: [
						{ name: "default", value: `transition-all` },
						{ name: "none", value: `transition-none` },
						{ name: "all", value: `transition-all` },
						{ name: "colors", value: `transition-colors` },
						{ name: "opacity", value: `transition-opacity` },
						{ name: "shadow", value: `transition-shadow` },
						{ name: "transform", value: `transition-transform` }
					]
				},

				transitionDuration: {
					group: "Transition",
					label: "Duration",
					values: Object.keys(tailwindConfig?.transitions ?? {}).map(key => ({
						name: this.getThemePropertyName("transitions", key),
						value: `duration-${key}`
					}))
				},

				transitionTimingFunction: {
					group: "Transition",
					label: "Timing Function",
					values: [
						{ name: "linear", value: `ease-linear` },
						{ name: "ease-in", value: `ease-in` },
						{ name: "ease-out", value: `ease-out` },
						{ name: "ease-in-out", value: `ease-in-out` }
					]
				},

				// Others
				display: {
					group: "Others",
					label: "Display",
					values: [
						{ name: "hidden", value: `hidden` },
						{ name: "block", value: `block` },
						{ name: "inline", value: `inline` },
						{ name: "inline-block", value: `inline-block` },
						{ name: "flex", value: `flex` },
						{ name: "inline-flex", value: `inline-flex` },
						{ name: "grid", value: `grid` },
						{ name: "inline-grid", value: `inline-grid` }
					]
				},

				cursor: {
					group: "Others",
					label: "Cursor",
					values: [
						{ name: "auto", value: `cursor-auto` },
						{ name: "default", value: `cursor-default` },
						{ name: "pointer", value: `cursor-pointer` },
						{ name: "wait", value: `cursor-wait` },
						{ name: "text", value: `cursor-text` },
						{ name: "move", value: `cursor-move` },
						{ name: "not-allowed", value: `cursor-not-allowed` }
					]
				},

				opacity: {
					group: "Others",
					label: "Opacity",
					values: [
						{ name: "100", value: `opacity-100` },
						{ name: "75", value: `opacity-75` },
						{ name: "50", value: `opacity-50` },
						{ name: "25", value: `opacity-25` },
						{ name: "0", value: `opacity-0` }
					]
				},

				inset: {
					group: "Others",
					label: "Inset",
					values: [
						{ name: "0", value: `inset-0` },
						{ name: "auto", value: `inset-auto` }
					]
				},

				zIndex: {
					group: "Others",
					label: "Z-index",
					values: [
						{ name: "0", value: `z-0` },
						{ name: "10", value: `z-10` },
						{ name: "20", value: `z-20` },
						{ name: "30", value: `z-30` },
						{ name: "40", value: `z-40` },
						{ name: "50", value: `z-50` },
						{ name: "auto", value: `z-auto` }
					]
				},
				// Hover
				fontWeightHover: {
					group: "Text",
					label: "Text Weight",
					values: [
						{ name: "hairline", value: `hover:font-hairline` },
						{ name: "thin", value: `hover:font-thin` },
						{ name: "light", value: `hover:font-light` },
						{ name: "normal", value: `hover:font-normal` },
						{ name: "medium", value: `hover:font-medium` },
						{ name: "semibold", value: `hover:font-semibold` },
						{ name: "bold", value: `hover:font-bold` },
						{ name: "extrabold", value: `hover:font-extrabold` },
						{ name: "black", value: `hover:font-black` }
					]
				},
				colorHover: {
					group: "Text",
					label: "Text Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `hover:text-${key}`
					}))
				},

				backgroundColorHover: {
					group: "Background",
					label: "Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `hover:bg-${key}`
					}))
				},

				boxShadowHover: {
					group: "Box",
					label: "Box Shadow",
					values: [
						{ name: "xs", value: `hover:shadow-xs` },
						{ name: "sm", value: `hover:shadow-sm` },
						{ name: "normal", value: `hover:shadow` },
						{ name: "md", value: `hover:shadow-md` },
						{ name: "lg", value: `hover:shadow-lg` },
						{ name: "xl", value: `hover:shadow-xl` },
						{ name: "2xl", value: `hover:shadow-2xl` },
						{ name: "inner", value: `hover:shadow-inner` },
						{ name: "outline", value: `hover:shadow-outline` },
						{ name: "none", value: `hover:shadow-none` }
					]
				},

				borderColorHover: {
					group: "Border",
					label: "Border Color",
					values: Object.keys(tailwindConfig?.colors ?? {}).map(key => ({
						name: this.getThemePropertyName("colors", key),
						value: `hover:border-${key}`
					}))
				},

				opacityHover: {
					group: "Others",
					label: "Opacity",
					values: [
						{ name: "100", value: `hover:opacity-100` },
						{ name: "75", value: `hover:opacity-75` },
						{ name: "50", value: `hover:opacity-50` },
						{ name: "25", value: `hover:opacity-25` },
						{ name: "0", value: `hover:opacity-0` }
					]
				}
			}
		},
		/**
		 * Group properties by Groups and Normal/Hover, for 'v-for' iteration.
		 * Output format: { normal: { group: [prop1, prop2], ... }, hover: { group: ... } }
		 */
		propertyGroups() {
			const output = { normal: {}, hover: {} }
			Object.keys(this.properties).map(key => {
				key.match(/Hover$/g) ? this.addToGroups(key, output.hover) : this.addToGroups(key, output.normal)
			})
			return output
		}
	},

	methods: {
		handleInput(property, event) {
			this.classesObject[this.selectedViewport][property] = event
			this.$emit("input", this.mergePrefixObjects(this.classesObject))
		},

		handleViewportChange(value) {
			this.selectedViewport = value
		},

		computedVModel(property) {
			const viewportObj = this.classesObject[this.selectedViewport]
			if (this.classesObject[this.selectedViewport]) {
				const output = viewportObj[property]
				return output
			} else return {}
		},
		/**
		 * Method to extract name based on class from tenant theming
		 */
		getThemePropertyName(targetProperty, key) {
			// Go through theme and find particular name for the color
			const value = tailwindConfig[targetProperty][key] // --> "var(--color1)"
			const token = value.replace(`var(--`, "").replace(`)`, "") // "var(--color1)" --> "color1"

			// Find name of the value from theming
			const propertyId = this.theme?.tokens?.[token]

			const name =
				targetProperty === "fontFamily"
					? this.theme?.typeFaces?.find(property => property.id === propertyId)?.name ?? "No Name"
					: this.theme[targetProperty]?.find(property => property.id === propertyId)?.name ?? "No Name"

			return name
		},

		/**
		 * Based on class determine which css  property it is
		 * e.g. "bg-c2" --> ?backgroundColor"; "text-xs" --> "fontSize"
		 */
		determineProperty(cls, properties = {}) {
			// When we can't find a property in properties, we add it into others category
			// It could be some classes that are not tailwind specific, we need to keep them
			// but we will not show UI for them
			return (
				Object.keys(properties).filter(property => properties?.[property]?.values.find(value => value?.value === cls))?.[0] ??
				"others"
			)
		},

		/**
		 * Compile classesObject into classesArray
		 */
		compileClassesObjectToClassArray(classesObject, prefix) {
			if (!classesObject) return
			const classesArray = Object.keys(classesObject)
				.map(key => classesObject[key])
				.join(" ")
				.trim()
				.split(" ")
			const output = classesArray.filter(Boolean)
			for (let i = 0; i < output.length; i++) {
				if (prefix) {
					output[i] = prefix + output[i]
				} else {
					output[i]
				}
			}
			return output
		},

		/**
		 * Compile classesString into classesObject
		 */
		compileClassStringToClassesObject(classesString) {
			if (!classesString) return
			const classes = classesString.split(" ")

			const output = {}

			classes.forEach(cls => {
				const property = this.determineProperty(cls, this.properties)

				// If others, merge others together
				if (property !== "others") output[property] = cls
				else output.others = `${output.others ?? ""}${cls} `
			})

			return output
		},

		mergePrefixObjects(classesObject) {
			const allClasses = classesObject.all ? this.compileClassesObjectToClassArray(classesObject.all) : ""
			const smClasses = classesObject.sm ? this.compileClassesObjectToClassArray(classesObject.sm, "sm:") : ""
			const mdClasses = classesObject.md ? this.compileClassesObjectToClassArray(classesObject.md, "md:") : ""
			const lgClasses = classesObject.lg ? this.compileClassesObjectToClassArray(classesObject.lg, "lg:") : ""
			const xlClasses = classesObject.xl ? this.compileClassesObjectToClassArray(classesObject.xl, "xl:") : ""

			const mergedOutput = [...allClasses, ...smClasses, ...mdClasses, ...lgClasses, ...xlClasses].join(" ")
			return mergedOutput
		},

		sortClassStringByPrefix(classesString) {
			const classes = classesString.split(" ")
			const prefixes = ["sm:", "md:", "lg:", "xl:"]

			const filterAll = arr => {
				if (!arr) return
				const result = arr.filter(cls => prefixes.indexOf(cls.slice(0, 3)) === -1)
				return result.filter(Boolean)
			}

			const allClasses = filterAll(classes).join(" ")

			const filterPrefix = (prefix, arr) => {
				if (!arr) return
				const strings = arr.filter(classString => classString.trim().slice(0, 3) === prefix)
				const result = strings.map(cls => cls.replace(prefix, ""))
				return result.filter(Boolean)
			}

			const smClasses = filterPrefix(prefixes[0], classes).join(" ")
			const mdClasses = filterPrefix(prefixes[1], classes).join(" ")
			const lgClasses = filterPrefix(prefixes[2], classes).join(" ")
			const xlClasses = filterPrefix(prefixes[3], classes).join(" ")

			this.classesObject = {
				all: this.compileClassStringToClassesObject(allClasses) ?? {},
				sm: this.compileClassStringToClassesObject(smClasses) ?? {},
				md: this.compileClassStringToClassesObject(mdClasses) ?? {},
				lg: this.compileClassStringToClassesObject(lgClasses) ?? {},
				xl: this.compileClassStringToClassesObject(xlClasses) ?? {}
			}
		},
		/**
		 * Add/Remove clicked group from 'selectedGroups' array to toggle collapses.
		 */
		clickGroups(group) {
			const groupIndex = this.selectedGroups.indexOf(group)
			groupIndex === -1 ? this.selectedGroups.push(group) : this.selectedGroups.splice(groupIndex, 1)
		},
		/**
		 * Helper function for 'propertyGroups'
		 * To put properties in corresponding Group array
		 */
		addToGroups(key, object) {
			const targetGroup = this.properties[key].group
			targetGroup in object ? object[targetGroup].push(key) : (object[targetGroup] = [key])
		}
	},

	mounted() {
		/**
		 * Pass all groups into 'selectedeGroups' on mounted, to expand all collapses.
		 */

		const propertySets = new Set(
			Object.keys(this.properties).map(property => {
				return this.properties[property].group
			})
		)
		this.selectedGroups = [...propertySets]
	},

	watch: {
		// When value changes, parse it
		value: {
			handler(newVal) {
				this.sortClassStringByPrefix(newVal)
			},
			immediate: true,
			deep: true
		}
	}
}
</script>
